import React from "react";
import {
  X,
  CheckCircle2,
  Zap,
  Star,
  Crown,
  Database,
  Calendar,
  CreditCard,
  Clock,
} from "lucide-react";
import { OfferingPlan } from "../../../../store/types/settings";
import formatPrice from "../../../../utility/formatPrice";

interface ViewPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: OfferingPlan | null;
}

const ViewPlanModal: React.FC<ViewPlanModalProps> = ({
  isOpen,
  onClose,
  plan,
}) => {
  if (!isOpen || !plan) return null;

  const getTierIcon = (tier: string | null) => {
    switch (tier?.toUpperCase()) {
      case "STARTER":
        return Zap;
      case "GROWTH":
        return Star;
      case "SCALE":
      case "ENTERPRISE":
        return Crown;
      default:
        return Database;
    }
  };

  const TierIcon = getTierIcon(plan.tier);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-primary/10 rounded-2xl text-primary">
              <TierIcon size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{plan.title}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs font-bold text-primary bg-primary/5 px-2 py-0.5 rounded-full uppercase tracking-wider">
                  {plan.serviceType}
                </span>
                <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full uppercase tracking-wider">
                  {plan.tier || "Standard"}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Overview */}
          <section>
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 ml-1">
              Description
            </h3>
            <div className="bg-gray-50 rounded-2xl p-3 border border-gray-100 relative group overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <TierIcon size={64} />
              </div>
              <p className="text-lg font-medium text-gray-700 leading-relaxed relative z-10">
                {plan.subtitle}
              </p>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed relative z-10">
                {plan.description}
              </p>
            </div>
          </section>

          {/* Pricing Details */}
          <section>
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 ml-1 flex items-center gap-2">
              <CreditCard size={16} /> Pricing & Setup Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-white border border-gray-100 rounded-2xl p-3 flex items-center gap-4 hover:border-primary/30 transition-all group shadow-sm">
                <div className="p-2 bg-emerald-50 rounded-xl text-emerald-600 group-hover:scale-110 transition-transform">
                  <CreditCard size={20} />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                    Setup Fee
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    ₦{formatPrice(parseFloat(plan.setupFee || "0"))}
                  </p>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl p-3 flex items-center gap-4 hover:border-primary/30 transition-all group shadow-sm">
                <div className="p-2 bg-amber-50 rounded-xl text-amber-600 group-hover:scale-110 transition-transform">
                  <Database size={20} />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                    Integration Cost
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    ₦{formatPrice(parseFloat(plan.integrationCost || "0"))}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="bg-white border-2 border-primary/10 rounded-2xl p-3 flex items-center gap-4 hover:border-primary/30 transition-all group">
                <div className="p-2 bg-primary/5 rounded-xl text-primary group-hover:scale-110 transition-transform">
                  <Calendar size={10} />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                    Monthly Billing
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    ₦{formatPrice(parseFloat(plan.monthlyPrice || "0"))}
                    <span className="text-sm text-gray-500 font-normal">
                      /mo
                    </span>
                  </p>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl p-3 flex items-center gap-4 hover:border-primary/30 transition-all group shadow-sm">
                <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600 group-hover:scale-110 transition-transform">
                  <Calendar size={10} />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                    Quarterly Billing
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    ₦
                    {formatPrice(
                      plan.quarterlyPrice
                        ? parseFloat(String(plan.quarterlyPrice))
                        : 0,
                    )}
                    <span className="text-sm text-gray-500 font-normal">
                      /qtr
                    </span>
                  </p>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl p-3 flex items-center gap-4 hover:border-primary/30 transition-all group shadow-sm">
                <div className="p-2 bg-blue-50 rounded-xl text-blue-600 group-hover:scale-110 transition-transform">
                  <Calendar size={10} />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                    Yearly Billing
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    ₦{formatPrice(parseFloat(plan.yearlyPrice || "0"))}
                    <span className="text-sm text-gray-500 font-normal">
                      /yr
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Features / Points */}
          <section>
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 ml-1 flex items-center gap-2">
              <CheckCircle2 size={16} /> Key Offerings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              {plan.points.map((point, index) => (
                <div key={index} className="flex items-start gap-3 group">
                  <div className="mt-1 p-0.5 bg-emerald-100 rounded-full text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                    <CheckCircle2 size={14} />
                  </div>
                  <span className="text-sm font-medium text-gray-600 group-hover:text-gray-900 transition-colors">
                    {point}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Deployment Cost Breakdown */}
          {plan.costBreakdown && (
            <section className="bg-gray-50 rounded-3xl p-6 border border-gray-100 flex flex-col gap-4">
              <div className="flex items-center justify-between border-b border-gray-200/50 pb-4">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  <Database size={16} /> Deployment Cost Breakdown
                </h3>
                <div className="flex flex-col items-end">
                  <p className="text-xs font-bold text-gray-400 uppercase">
                    Total Setup
                  </p>
                  <p className="text-xl font-bold text-primary">
                    ₦{formatPrice(plan.costBreakdown.totalDeploymentCost)}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {plan.costBreakdown.deploymentCosts.map((cost) => (
                  <div
                    key={cost.id}
                    className="flex justify-between items-start group"
                  >
                    <div className="flex-1 pr-4">
                      <p className="text-sm font-bold text-gray-800 group-hover:text-primary transition-colors">
                        {cost.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                        {cost.description}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">
                        ₦{formatPrice(cost.price)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200/50 flex items-center justify-between text-xs text-gray-400">
                <div className="flex items-center gap-1.5 font-medium">
                  <Clock size={12} />
                  Last Updated:{" "}
                  {new Date(
                    plan.costBreakdown.lastUpdated,
                  ).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-1.5 font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg">
                  <CheckCircle2 size={12} /> Fully Integrated
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Footer info */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-center gap-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          <span>Plan ID: {plan.id}</span>
          <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
          <span>Status: {plan.isActive ? "Active" : "Inactive"}</span>
          <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
          <span>Updated: {new Date(plan.updatedAt).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
};

export default ViewPlanModal;
